import { Switch } from '@headlessui/react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import * as Yup from 'yup';
import { useInvoiceMutations, useInvoices } from 'entities/invoice/model';
import { useCurrentUserDetails } from 'entities/user/model';
import { Field, Form, Formik, ErrorMessage } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';

import { TextInput } from 'shared/ui/form/Input';
import useToggle from 'shared/hooks/useToggle';
import {
  usePaymentDetails,
  usePaymentDetailsMutations,
} from 'entities/organization/';
import { Alert } from 'shared/ui/Alert/Alert';
import { InvoiceLineItems } from './InvoiceGenerateForm.LineItems';
import { useWeb3Options } from 'widgets/Users/UserBusinessTypeSetting';

import { Button } from 'shared/ui/Button/Button';
import { v4 as uuidv4 } from 'uuid';

import {
  useOrganizationClients,
  useInvalidateOrganizationClients,
} from 'features/organization/clients/useClients';

import { PencilIcon, PlusIcon } from '@heroicons/react/24/outline';
import { countries, filterOption } from 'shared/ui/form/CountrySelect';
import { SelectClientFormPart } from 'features/invoice/createNew/SelectClientFormPart';
import { SuccessModal } from 'features/invoice/createNew/SuccessModal';
import { FailModal } from 'features/invoice/createNew/FailModal';
import { useInvoiceActions } from 'entities/invoice/model';
import { PaymentDetailsCollectionStepper } from 'features/payments/paymentDetails/PaymentDetailsCollectionStepper';

import { useInvalidateRecurringInvoices } from 'features/invoice/recurring/useRecurringInvoices';
import {
  getTokenHumanReadableNameByChain,
  upperCaseNetworkType,
} from '../../features/bill/lib';
import { ROLES } from '../../shared/hooks';
import { ScrollToError } from '../../shared/ui/form/ScrollToError';
import {
  Form as AForm,
  Radio,
  Input as AInput,
  Typography,
  Select,
} from 'antd';

const { Text } = Typography;

export function generateInvoiceId() {
  const date = new Date();
  const datePart = `${String(date.getDate()).padStart(2, '0')}${String(
    date.getMonth() + 1
  ).padStart(2, '0')}${String(date.getFullYear()).slice(-2)}`;
  const randomPart = uuidv4().substr(0, 5);
  return `INV-${datePart}-${randomPart}`.toUpperCase();
}

const FIAT_PAYMENT_FORM_VALUES = {
  paymentMethod: 'fiat',
};

const FormSchema = Yup.object().shape({
  clientName: Yup.string().required('Client name is required'),
  clientEmail: Yup.string()
    .email('Invalid client email address format')
    .required('Client email is required'),

  note: Yup.string().max(550, 'Please enter less then 550 characters'),
});

const DEFAULT_FORM_VALUES = {
  // user_data
  userName: '',
  userEmail: '',
  userCountry: '',
  userCity: '',
  userStreetAddress: '',

  // client_data
  clientName: '',
  clientEmail: '',
  clientCountry: undefined,
  clientCity: '',
  clientId: undefined,

  // invoice_data
  invoiceID: generateInvoiceId(),
  invoiceDate: new Date().toISOString().slice(0, 10),
  invoiceDueDate: new Date().toISOString().slice(0, 10),
  paymentMethod: 'crypto',
  selectedPaymentDetails: {},

  recurrenceRuleEnabled: false,
  recurrenceRuleInterval: 'month',
  recurrenceRuleDuration: 2,
  lineItems: [],
  note: '',
  includeNote: false,
};

const preparePaymwntDetailsPayload = (
  userData,
  { cryptoWallet, bankAccount }
) => {
  const { newWalletReference, address, ensAddress } = cryptoWallet || {};
  const {
    newAccountReference,

    accountBeneficiary,
    bankAccountBeneficiaryType,
    accountNumber,
    accountIBAN,
    accountACHRoutingNumber,
    bankName,
    bankAddress,
    bankBIC,
  } = bankAccount || {};

  return {
    name: newWalletReference || newAccountReference,
    type: cryptoWallet ? 'crypto_wallet' : 'bank',
    businessRelation: 'client',
    details: cryptoWallet
      ? {
          cryptoWallet: {
            address,
            ensAddress,
          },
        }
      : {
          bankAccount: {
            accountBeneficiary,
            bankAccountBeneficiaryType,
            accountNumber,
            accountIBAN,
            accountACHRoutingNumber,
            bankName,
            bankAddress,
            bankBIC,
          },
        },
    m_ownerUserId: userData.user_id,
    m_ownerOrganisationId: userData.selected_organization?._id,
  };
};

const CreateInvoiceForm = ({ invoice: invoiceToUpdate }) => {
  const isEditMode = !!invoiceToUpdate;

  const { updateAction: updateInvoice } = useInvoiceActions();
  const { web3OptionsEnabled } = useWeb3Options();
  const { invalidateRecurringInvoices } = useInvalidateRecurringInvoices();
  const { data, getCurrentOrganizationId, getCurrentOrgRole } =
    useCurrentUserDetails();
  const { clients } = useOrganizationClients();
  const { invalidateOrganizationClients } = useInvalidateOrganizationClients();

  const { data: paymentDetails } = usePaymentDetails();
  const { createPaymentDetails } = usePaymentDetailsMutations();

  const { data: invoices } = useInvoices();
  const { create } = useInvoiceMutations();
  const [pdSelectionModalOpened, setPdSelectionModalOpened] = useState(false);

  const initialValues = useMemo(() => {
    if (invoiceToUpdate) {
      return mapInvoiceToFormValues(invoiceToUpdate, clients);
    }

    const preFillData = data.selected_organization
      ? mapOrganationToFormDTO(data.selected_organization)
      : invoices && invoices[invoices.length - 1]
      ? mapInvoiceToFormDTO(invoices[invoices.length - 1])
      : {};

    return {
      ...DEFAULT_FORM_VALUES,
      invoiceID: generateInvoiceId(),
      ...preFillData,
      ...(web3OptionsEnabled ? {} : FIAT_PAYMENT_FORM_VALUES),
    };
  }, [
    clients,
    invoiceToUpdate,
    data.selected_organization,
    invoices,
    web3OptionsEnabled,
  ]);

  const [includeUserDetails, toggleIncludeUserDetails] = useToggle(
    !!invoiceToUpdate?.user_first_line_address || false
  );
  const [includeClientDetails, toggleIncludeClientDetails] = useToggle(
    !!invoiceToUpdate?.client_first_line_address || false
  );
  const [invoiceSuccessData, setInvoiceSuccessData] = useState(null);
  const [invoiceFailFlag, setInvoiceFailFlag] = useState(false);

  const mapFormDTO2Body = useCallback(
    (invoice) => {
      const { cryptoWallet, bankAccount } = invoice.selectedPaymentDetails;
      const res = {
        organizationId: getCurrentOrganizationId(),
        user_data: {
          name: invoice.userName,
          email: invoice.userEmail,
          street_address: invoice.userStreetAddress,
          city: invoice.userCity,
          country: invoice.userCountry,
          postcode: invoice.userPostcode,
          vat_number: invoice.userVatNumber,
        },
        client_data: {
          name: invoice.clientName,
          email: invoice.clientEmail,
          street_address: invoice.clientStreetAddress || '',
          city: invoice.clientCity || '',
          country: invoice.clientCountry || '',
          postcode: invoice.clientPostcode || '',
          vat_number: invoice.clientVatNumber || '',
        },
        invoice_data: {
          id: invoice.invoiceID,
          date: invoice.invoiceDate,
          due_date: invoice.invoiceDueDate,
          token: cryptoWallet?.currency || bankAccount?.currency,
          network: cryptoWallet?.network || 'Fiat',
          wallet_address: cryptoWallet?.address,
          optionalWallets: cryptoWallet?.optionalNetworksCurrencies.map(
            ({ network, currency }) => ({
              address: cryptoWallet?.address,
              network: network.toLowerCase(),
              token: currency,
            })
          ),
          bank_account_name_beneficiary: bankAccount?.accountBeneficiary,
          bankAccountBeneficiaryType: bankAccount?.bankAccountBeneficiaryType,
          bank_name: bankAccount?.bankName,
          bank_address: bankAccount?.bankAddress,
          bic: bankAccount?.bankBIC,
          iban: bankAccount?.accountIBAN,
          ach_routing_number: bankAccount?.accountACHRoutingNumber,
          account_number: bankAccount?.accountNumber,

          ens_check: !!cryptoWallet?.ensAddress,
          ens_name: cryptoWallet?.ensAddress,

          lineItems: invoice.lineItems.map((item) => ({
            description: item.description,
            quantity: String(item.quantity),
            amount: String(item.amount),
            taxRate: String(item.taxRate),
            totalAmount: String(item.totalAmount),
          })),
        },

        note: invoice.note,
      };

      if (invoice.recurrenceRuleEnabled) {
        res.recurrenceRule = {
          interval: invoice.recurrenceRuleInterval,
          duration: Number(invoice.recurrenceRuleDuration),
        };
      }

      return res;
    },
    [getCurrentOrganizationId, includeClientDetails, includeUserDetails]
  );

  const _resetPaymentDetails = (setFieldValue) => {
    setFieldValue('selectedPaymentDetails', {});
  };

  const _getPdsTypesForStepper = (paymentMethod) => {
    switch (paymentMethod) {
      case 'crypto':
        return ['crypto-wallet'];
      case 'fiat':
        return ['bank-account'];
      case 'multiple':
        return ['crypto-wallet', 'bank-account'];
      default:
        return [];
    }
  };

  const handleSubmit = useCallback(
    async (values, { setSubmitting, setFieldValue }) => {
      setSubmitting(true);

      const formData = mapFormDTO2Body(values);

      try {
        if (
          values?.selectedPaymentDetails?.cryptoWallet?.newWalletReference ||
          values?.selectedPaymentDetails?.bankAccount?.newAccountReference
        ) {
          const promises = [];

          if (
            values?.selectedPaymentDetails?.cryptoWallet?.newWalletReference
          ) {
            promises.push(
              createPaymentDetails.mutateAsync(
                preparePaymwntDetailsPayload(data, {
                  cryptoWallet: values?.selectedPaymentDetails?.cryptoWallet,
                })
              )
            );
          }

          if (
            values?.selectedPaymentDetails?.bankAccount?.newAccountReference
          ) {
            promises.push(
              createPaymentDetails.mutateAsync(
                preparePaymwntDetailsPayload(data, {
                  bankAccount: values?.selectedPaymentDetails?.bankAccount,
                })
              )
            );
          }

          await Promise.all(promises);
        }

        let res;

        if (isEditMode) {
          res = await updateInvoice(invoiceToUpdate._id, formData);
        } else {
          res = await create.mutateAsync(formData);
        }

        if (
          values?.selectedPaymentDetails?.cryptoWallet?.newWalletReference ||
          values?.selectedPaymentDetails?.bankAccount?.newAccountReference
        ) {
          await invalidateOrganizationClients();
        }

        if (formData.recurrenceRule) {
          await invalidateRecurringInvoices();
        }

        if (res.status === 200) {
          setInvoiceSuccessData(
            res.data?.result
              ? {
                  ...res?.data?.result,
                  clientEmail: values.clientEmail,
                  issuerName: values.userName,
                  isEditMode,
                }
              : {
                  id: invoiceToUpdate._id,
                  clientEmail: values.clientEmail,
                  issuerName: values.userName,
                  isEditMode,
                }
          );
          setFieldValue('invoiceID', generateInvoiceId());
        }
      } catch (error) {
        console.error(error);
        setInvoiceFailFlag(true);
      } finally {
        setSubmitting(false);
      }
    },
    [mapFormDTO2Body, create]
  );

  const resolveInvoiceCurrency = (values) => {
    let cur =
      values.selectedPaymentDetails?.cryptoWallet?.currency ||
      values.selectedPaymentDetails?.bankAccount?.currency;

    if (cur) {
      return cur;
    }

    if (
      values.paymentMethod === 'crypto' ||
      values.paymentMethod === 'multiple'
    ) {
      return 'USDC';
    }

    return 'USD';
  };

  return (
    <>
      <div className="mb-10 md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-3">
          <div className="px-4 sm:px-0">
            <h3 className="text-lg font-medium leading-6 text-slate-900 dark:text-white ">
              {isEditMode ? 'Edit Invoice' : 'Create Invoice'}
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-slate-400">
              {isEditMode
                ? 'Please change the details of the invoice below.'
                : `Fill in this form to create an invoice. All invoices follow US &
              European accounting guidelines.`}
            </p>
          </div>
        </div>
      </div>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={FormSchema}
        validate={(values) => {
          const errors = {};
          if (!values.lineItems?.length) {
            errors.lineItems = 'At least one service item is required';
          }

          if (Object.keys(values.selectedPaymentDetails).length === 0) {
            errors.selectedPaymentDetails = 'Payment details are required';
          }

          if (
            values.paymentMethod === 'fiat' &&
            (!values.userStreetAddress ||
              !values.userCity ||
              !values.userCountry ||
              !values.userPostcode)
          ) {
            if (!values.userStreetAddress) {
              errors.userStreetAddress =
                'Street address is required for fiat invoice';
            }

            if (!values.userCity) {
              errors.userCity = 'City is required for fiat invoice';
            }

            if (!values.userCountry) {
              errors.userCountry = 'Country is required for fiat invoice';
            }

            if (!values.userPostcode) {
              errors.userPostcode = 'Postal code is required for fiat invoice';
            }
          }

          return errors;
        }}
        onSubmit={handleSubmit}>
        {({ isSubmitting, values, errors, setFieldValue, validateForm }) => (
          <Form className="invoice-form space-y-6">
            {/* *************** COMPANY DETAILS ********************** */}
            <div className="bg-white px-4 py-5 shadow dark:bg-slate-800 sm:rounded-lg sm:p-6">
              <div className="md:grid md:grid-cols-3 md:gap-6">
                <div className="md:col-span-1">
                  <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                    Your Details
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-slate-300">
                    Please input details about yourself or your company. This
                    information will be displayed in the invoice.
                  </p>
                </div>
                <div className="mt-5 space-y-6 md:col-span-2 md:mt-0">
                  <div className="grid grid-cols-6 gap-6">
                    <AForm.Item
                      className="col-span-6 sm:col-span-3"
                      label="Name"
                      layout="vertical"
                      help="This can be your name or name of your company.">
                      <AInput
                        onChange={(evt) => {
                          setFieldValue('userName', evt.target.value);
                        }}
                        size="large"
                        value={values.userName}
                      />
                    </AForm.Item>

                    <AForm.Item
                      className="col-span-6 sm:col-span-3"
                      label="Email"
                      layout="vertical"
                      help="This will be displayed on the invoice.">
                      <AInput
                        onChange={(evt) => {
                          setFieldValue('userEmail', evt.target.value);
                        }}
                        size="large"
                        value={values.userEmail}
                      />
                    </AForm.Item>
                  </div>

                  <div className="grid grid-cols-6 gap-6">
                    <div className="col-span-6 sm:col-span-3">
                      <Switch.Group as="div" className="items-left flex ">
                        <Switch.Label
                          as="span"
                          className="mr-5 text-sm font-medium leading-6 text-slate-300"
                          passive>
                          Include Your Address
                        </Switch.Label>
                        <Switch
                          checked={includeUserDetails}
                          onChange={toggleIncludeUserDetails}
                          className={classNames(
                            includeUserDetails
                              ? 'bg-indigo-600'
                              : 'bg-gray-500',
                            'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2'
                          )}>
                          <span
                            aria-hidden="true"
                            className={classNames(
                              includeUserDetails
                                ? 'translate-x-5'
                                : 'translate-x-0',
                              'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                            )}
                          />
                        </Switch>
                      </Switch.Group>
                    </div>
                  </div>

                  {(includeUserDetails ||
                    !!errors.userStreetAddress ||
                    !!errors.userCountry ||
                    !!errors.userPostcode ||
                    !!errors.userCity) && (
                    <>
                      <div
                        className={classNames('grid grid-cols-6 gap-6', {
                          'formik-error-message':
                            !!errors.userStreetAddress || !!errors.userCity,
                        })}>
                        <AForm.Item
                          className="col-span-6 sm:col-span-3"
                          label="House & Street Name"
                          layout="vertical"
                          validateStatus={
                            errors.userStreetAddress ? 'error' : undefined
                          }
                          help={'First line of address.'}>
                          <AInput
                            size="large"
                            value={values.userStreetAddress}
                            onChange={(evt) => {
                              setFieldValue(
                                'userStreetAddress',
                                evt.target.value
                              );
                            }}
                          />
                        </AForm.Item>
                        <AForm.Item
                          className="col-span-6 sm:col-span-3"
                          label="City"
                          layout="vertical"
                          validateStatus={errors.userCity ? 'error' : undefined}
                          help={'City where business is registered.'}>
                          <AInput
                            size="large"
                            value={values.userCity}
                            onChange={(evt) => {
                              setFieldValue('userCity', evt.target.value);
                            }}
                          />
                        </AForm.Item>
                      </div>

                      <div
                        className={classNames('grid grid-cols-6 gap-6', {
                          'formik-error-message':
                            !!errors.userCountry || !!errors.userPostcode,
                        })}>
                        <AForm.Item
                          className="col-span-6 sm:col-span-3"
                          layout="vertical"
                          validateStatus={
                            errors.userCountry ? 'error' : undefined
                          }
                          help={'Country of business.'}
                          rules={[
                            {
                              required: true,
                              message: 'Select bank country',
                            },
                          ]}
                          label="Country">
                          <Select
                            size="large"
                            showSearch
                            placeholder={'Select country'}
                            filterOption={filterOption}
                            onChange={(val) => {
                              setFieldValue('userCountry', val);
                            }}
                            value={values.userCountry}
                            // @ts-ignore
                            rules={[
                              {
                                required: true,
                                message: 'Select country',
                              },
                            ]}
                            options={countries.map(
                              ({ label, value, flag }) => ({
                                value,
                                label: (
                                  <>
                                    {flag} {label}
                                  </>
                                ),
                              })
                            )}
                          />
                        </AForm.Item>

                        <AForm.Item
                          className="col-span-6 sm:col-span-3"
                          label="ZIP Code"
                          validateStatus={
                            errors.userPostcode ? 'error' : undefined
                          }
                          layout="vertical"
                          help={'Business ZIP / post code.'}>
                          <AInput
                            onChange={(evt) => {
                              setFieldValue('userPostcode', evt.target.value);
                            }}
                            size="large"
                            value={values.userPostcode}
                          />
                        </AForm.Item>
                      </div>

                      <div className="grid grid-cols-6 gap-6">
                        <AForm.Item
                          className="col-span-6 sm:col-span-3"
                          label="VAT / EIN Number"
                          layout="vertical"
                          help="Please input the VAT / EIN number. If you don't have one, leave">
                          <AInput
                            size="large"
                            onChange={(evt) => {
                              setFieldValue('userVatNumber', evt.target.value);
                            }}
                            value={values.userVatNumber}
                          />
                        </AForm.Item>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* *************** CLIENT DETAILS ********************** */}
            <div className="bg-white px-4 py-5 shadow dark:bg-slate-800 sm:rounded-lg sm:p-6">
              <div className="md:grid md:grid-cols-3 md:gap-6">
                <div className="md:col-span-1">
                  <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                    Client Details
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-slate-300">
                    Please input details of the client that you want to invoice.
                  </p>
                </div>
                <div className="mt-5 space-y-6 md:col-span-2 md:mt-0">
                  <SelectClientFormPart
                    includeClientDetails={includeClientDetails}
                    toggleIncludeClientDetails={toggleIncludeClientDetails}
                    clients={clients}
                    values={values}
                    setFieldValue={setFieldValue}
                    validateForm={validateForm}
                    isEditMode={isEditMode}
                  />
                </div>
              </div>
            </div>

            {/* *************** Invoice Details ********************** */}
            <div className="bg-white px-4 py-5 shadow dark:bg-slate-800 sm:rounded-lg sm:p-6">
              <div className="md:grid md:grid-cols-3 md:gap-6">
                <div className="md:col-span-1">
                  <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                    Invoice Details
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-slate-300">
                    Use this space to input the details of the invoice.
                  </p>
                </div>
                <div className="mt-5 md:col-span-2 md:mt-0">
                  <div className="grid grid-cols-6 gap-6">
                    <AForm.Item
                      className="col-span-6 sm:col-span-3"
                      label="Invoice ID"
                      layout="vertical"
                      help="Autogenerated Invoice ID. You can change it if you
                            require a custom ID.">
                      <AInput
                        size="large"
                        onChange={(evt) => {
                          setFieldValue('invoiceID', evt.target.value);
                        }}
                        value={values.invoiceID}
                      />
                    </AForm.Item>

                    <AForm.Item
                      className="col-span-6 sm:col-span-3"
                      label="Payment Terms"
                      layout="vertical">
                      <Select
                        id="paymentTerms"
                        name="paymentTerms"
                        size="large"
                        placeholder={'Select payment terms'}
                        onChange={(val) => {
                          const invoiceDate = new Date(values.invoiceDate);
                          invoiceDate.setDate(
                            invoiceDate.getDate() + Number(val)
                          );

                          const dueDate = invoiceDate
                            .toISOString()
                            .split('T')[0];

                          setFieldValue('invoiceDueDate', dueDate);
                          setFieldValue('paymentTerms', val);
                        }}
                        value={values.paymentTerms}
                        // @ts-ignore
                        rules={[
                          {
                            required: true,
                            message: 'Select payment terms',
                          },
                        ]}
                        options={[
                          { value: '0', label: 'Due on Receipt' },
                          { value: '7', label: '7 Days' },
                          { value: '10', label: '10 Days' },
                          { value: '14', label: '14 Days' },
                          { value: '30', label: '30 Days' },
                        ]}
                      />
                    </AForm.Item>

                    {!isEditMode && (
                      <div className="col-span-6">
                        <Switch.Group
                          as="div"
                          className="flex items-center justify-between">
                          <Switch
                            checked={values.recurrenceRuleEnabled}
                            onChange={(checked) =>
                              setFieldValue('recurrenceRuleEnabled', checked)
                            }
                            className={classNames(
                              values.recurrenceRuleEnabled
                                ? 'bg-primary-600'
                                : 'bg-gray-500',
                              'relative mr-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2'
                            )}>
                            <span
                              aria-hidden="true"
                              className={classNames(
                                values.recurrenceRuleEnabled
                                  ? 'translate-x-5'
                                  : 'translate-x-0',
                                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                              )}
                            />
                          </Switch>
                          <span className="flex flex-grow flex-col">
                            <Switch.Label
                              as="span"
                              className="text-sm font-medium leading-6 text-[#CBD5E1]"
                              passive>
                              Repeat Invoice
                            </Switch.Label>
                            <Switch.Description
                              as="span"
                              className="text-sm text-[#94A3B8]">
                              Enable to repeat this invoice every month or week
                            </Switch.Description>
                          </span>
                        </Switch.Group>
                      </div>
                    )}

                    {values.recurrenceRuleEnabled && (
                      <>
                        <div className="col-span-6 sm:col-span-3">
                          <label
                            htmlFor="recurrenceRuleInterval"
                            className="block text-sm font-medium text-[#CBD5E1]">
                            Frequency
                          </label>
                          <div className="mt-1 flex rounded-md shadow-sm">
                            <Field
                              as="select"
                              name="recurrenceRuleInterval"
                              id="recurrenceRuleInterval"
                              className="mt-1 block w-full rounded-md border-2 border-[#4E6180] bg-[#18222d] text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                              <option value="month">Every Month</option>
                              <option value="week">Every Week</option>
                            </Field>
                          </div>
                        </div>

                        <div className="col-span-6 sm:col-span-3">
                          <label
                            htmlFor="recurrenceRuleDuration"
                            className="block text-sm font-medium text-[#CBD5E1]">
                            Repeat For
                          </label>
                          <div className="mt-1 flex rounded-md shadow-sm">
                            <Field
                              as="select"
                              name="recurrenceRuleDuration"
                              id="recurrenceRuleDuration"
                              className="mt-1 block w-full rounded-md border-2 border-[#4E6180]    bg-[#18222d] text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                              <option value="2">
                                2{' '}
                                {getRecurrenceUnit(
                                  values.recurrenceRuleInterval
                                )}
                              </option>
                              <option value="3">
                                3{' '}
                                {getRecurrenceUnit(
                                  values.recurrenceRuleInterval
                                )}
                              </option>
                              <option value="4">
                                4{' '}
                                {getRecurrenceUnit(
                                  values.recurrenceRuleInterval
                                )}
                              </option>
                              <option value="5">
                                5{' '}
                                {getRecurrenceUnit(
                                  values.recurrenceRuleInterval
                                )}
                              </option>
                              <option value="6">
                                6{' '}
                                {getRecurrenceUnit(
                                  values.recurrenceRuleInterval
                                )}
                              </option>
                              <option value="7">
                                7{' '}
                                {getRecurrenceUnit(
                                  values.recurrenceRuleInterval
                                )}
                              </option>
                              <option value="8">
                                8{' '}
                                {getRecurrenceUnit(
                                  values.recurrenceRuleInterval
                                )}
                              </option>
                              <option value="9">
                                9{' '}
                                {getRecurrenceUnit(
                                  values.recurrenceRuleInterval
                                )}
                              </option>
                              <option value="10">
                                10{' '}
                                {getRecurrenceUnit(
                                  values.recurrenceRuleInterval
                                )}
                              </option>
                              <option value="11">
                                11{' '}
                                {getRecurrenceUnit(
                                  values.recurrenceRuleInterval
                                )}
                              </option>
                              <option value="12">
                                12{' '}
                                {getRecurrenceUnit(
                                  values.recurrenceRuleInterval
                                )}
                              </option>
                            </Field>
                          </div>
                        </div>
                      </>
                    )}

                    <AForm.Item
                      className="col-span-6 sm:col-span-3"
                      label="Invoice Date"
                      layout="vertical"
                      help="Date when you issued the invoice.">
                      <TextInput
                        id="invoiceDate"
                        name="invoiceDate"
                        type="date"
                        color={'darkSlate'}
                        value={values.invoiceDate}
                        onChange={(evt) => {
                          setFieldValue('invoiceDate', evt.target.value);
                        }}
                      />
                    </AForm.Item>

                    <AForm.Item
                      className="col-span-6 sm:col-span-3"
                      label="Payment Date"
                      layout="vertical"
                      help="Date when the invoice is due.">
                      <TextInput
                        id="invoiceDueDate"
                        name="invoiceDueDate"
                        type="date"
                        color={'darkSlate'}
                        value={values.invoiceDueDate}
                        onChange={(evt) => {
                          setFieldValue('invoiceDueDate', evt.target.value);
                        }}
                      />
                    </AForm.Item>

                    <div className="col-span-6 pt-2" />

                    {/* *************** Payent Types ********************** */}
                    {web3OptionsEnabled && (
                      <fieldset className="col-span-6">
                        <AForm.Item
                          label="How would you like to be paid?"
                          help={
                            <Text
                              className={'!text-slate-400 mt-2 block'}
                              italic={true}>
                              {values.paymentMethod === 'crypto' &&
                                'For crypto payments (e.g. USDC), your wallet details\n' +
                                  '                          will be displayed on the invoice.'}
                              {values.paymentMethod === 'fiat' &&
                                'For fiat payments (e.g., USD, GBP), your bank\n' +
                                  '                                details will appear on the invoice.'}

                              {values.paymentMethod === 'multiple' &&
                                'Both your crypto and bank details will be\n' +
                                  '                                displayed on the invoice.'}
                            </Text>
                          }
                          layout={'vertical'}>
                          <Radio.Group
                            value={values.paymentMethod}
                            onChange={(evt) => {
                              setFieldValue('paymentMethod', evt.target.value);
                              _resetPaymentDetails(setFieldValue);

                              if (
                                evt.target.value === 'fiat' &&
                                !includeUserDetails
                              ) {
                                toggleIncludeUserDetails();
                              }
                            }}>
                            <Radio.Button value="crypto">
                              Crypto Payment
                            </Radio.Button>
                            <Radio.Button value="fiat">
                              Fiat Payment
                            </Radio.Button>
                            <Radio.Button value="multiple">
                              Multiple Methods
                            </Radio.Button>
                          </Radio.Group>
                        </AForm.Item>
                      </fieldset>
                    )}

                    {/* *************** Selected Payment Methods Summary ********************** */}

                    {values.selectedPaymentDetails.cryptoWallet ||
                    values.selectedPaymentDetails.bankAccount ? (
                      <div className="col-span-6">
                        <Alert color="slate">
                          <span className="text-slate-400">Currency: </span>
                          <span>
                            {getTokenHumanReadableNameByChain(
                              resolveInvoiceCurrency(values),
                              values?.selectedPaymentDetails?.cryptoWallet
                                ?.network
                            )}
                          </span>
                          <br />{' '}
                          {values.selectedPaymentDetails.cryptoWallet && (
                            <>
                              <span className="text-slate-400">Network: </span>
                              <span>
                                {
                                  values.selectedPaymentDetails.cryptoWallet
                                    .network
                                }
                              </span>
                              <br />{' '}
                              <span className="text-slate-400">Address: </span>
                              <span>
                                {
                                  values.selectedPaymentDetails.cryptoWallet
                                    .address
                                }
                              </span>
                              {!!values.selectedPaymentDetails.cryptoWallet
                                .ensAddress && (
                                <>
                                  <br />{' '}
                                  <span className="text-slate-400">
                                    ENS Address:
                                  </span>{' '}
                                  <span className="dark:text-slate-300">
                                    {
                                      values.selectedPaymentDetails.cryptoWallet
                                        .ensAddress
                                    }
                                  </span>
                                </>
                              )}
                              {values.selectedPaymentDetails.bankAccount && (
                                <>
                                  <br />{' '}
                                </>
                              )}
                            </>
                          )}
                          {values.selectedPaymentDetails.bankAccount && (
                            <>
                              {!!values.selectedPaymentDetails.bankAccount
                                .accountBeneficiary && (
                                <>
                                  <span className="text-slate-400">
                                    Beneficiary:{' '}
                                  </span>
                                  <span className="dark:text-slate-300">
                                    {
                                      values.selectedPaymentDetails.bankAccount
                                        .accountBeneficiary
                                    }
                                  </span>
                                </>
                              )}
                              {!!values.selectedPaymentDetails.bankAccount
                                .accountNumber && (
                                <>
                                  <br />{' '}
                                  <span className="text-slate-400">
                                    Account Number:{' '}
                                  </span>
                                  <span className="dark:text-slate-300">
                                    {
                                      values.selectedPaymentDetails.bankAccount
                                        .accountNumber
                                    }
                                  </span>
                                </>
                              )}
                              {!!values.selectedPaymentDetails.bankAccount
                                .accountIBAN && (
                                <>
                                  <br />{' '}
                                  <span className="text-slate-400">IBAN: </span>
                                  <span className="dark:text-slate-300">
                                    {
                                      values.selectedPaymentDetails.bankAccount
                                        .accountIBAN
                                    }
                                  </span>
                                </>
                              )}
                              {!!values.selectedPaymentDetails.bankAccount
                                .accountACHRoutingNumber && (
                                <>
                                  <br />{' '}
                                  <span className="text-slate-400">
                                    Routing Number:{' '}
                                  </span>
                                  <span className="dark:text-slate-300">
                                    {
                                      values.selectedPaymentDetails.bankAccount
                                        .accountACHRoutingNumber
                                    }
                                  </span>
                                </>
                              )}
                              {!!values.selectedPaymentDetails.bankAccount
                                .bankName && (
                                <>
                                  <br />{' '}
                                  <span className="text-slate-400">
                                    Bank Name:{' '}
                                  </span>
                                  <span className="dark:text-slate-300">
                                    {
                                      values.selectedPaymentDetails.bankAccount
                                        .bankName
                                    }
                                  </span>
                                </>
                              )}
                            </>
                          )}
                          <Button
                            onClick={() => setPdSelectionModalOpened(true)}
                            size="xs"
                            color="gray"
                            className="mt-3">
                            <PencilIcon width={12} className="mr-2" />
                            Edit Payment Details
                          </Button>
                        </Alert>
                      </div>
                    ) : (
                      <div className="col-span-6">
                        <Button
                          onClick={() => setPdSelectionModalOpened(true)}
                          size="sm"
                          color="slate"
                          className="mt-3">
                          <PlusIcon
                            width={16}
                            className="text-indigo-400 mr-2"
                          />
                          Add Payment Details
                        </Button>
                      </div>
                    )}

                    <PaymentDetailsCollectionStepper
                      allowedManualInput={getCurrentOrgRole() === ROLES.ADMIN}
                      show={pdSelectionModalOpened}
                      walletList={(paymentDetails?.result || [])
                        .filter((p) => p.type === 'crypto_wallet')
                        .map((pd) => ({
                          ...pd.details.cryptoWallet,
                          name: pd.name,
                        }))}
                      bankAcountList={(paymentDetails?.result || [])
                        .filter((p) => p.type === 'bank')
                        .map((pd) => ({
                          ...pd.details.bankAccount,
                          name: pd.name,
                        }))}
                      selectedPymentDetails={values.selectedPaymentDetails}
                      onPaymentDetailsSelect={(vl) => {
                        setFieldValue('selectedPaymentDetails', vl);
                      }}
                      onClose={() => setPdSelectionModalOpened(false)}
                      pdsTypes={_getPdsTypesForStepper(values.paymentMethod)}
                    />

                    <div className="col-span-6">
                      <ErrorMessage
                        name="selectedPaymentDetails"
                        component="div"
                        className="mt-2 text-xs text-rose-500"
                      />
                    </div>

                    <div className="col-span-6">
                      <Switch.Group
                        as="div"
                        className="flex items-center justify-between">
                        <Switch
                          checked={values.includeNote}
                          onChange={(checked) => {
                            setFieldValue('includeNote', checked);
                          }}
                          className={classNames(
                            values.includeNote
                              ? 'bg-primary-600'
                              : 'bg-gray-500',
                            'relative mr-4 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2'
                          )}>
                          <span
                            aria-hidden="true"
                            className={classNames(
                              values.includeNote
                                ? 'translate-x-5'
                                : 'translate-x-0',
                              'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                            )}
                          />
                        </Switch>
                        <span className="flex flex-grow flex-col">
                          <Switch.Label
                            as="span"
                            className="text-sm font-medium leading-6 text-[#CBD5E1]"
                            passive>
                            Invoice Memo
                          </Switch.Label>
                          <Switch.Description
                            as="span"
                            className="text-sm text-[#94A3B8]">
                            Add custom information that you would like to
                            display in an invoice
                          </Switch.Description>
                        </span>
                      </Switch.Group>

                      {values.includeNote && (
                        <div className="mt-5 md:col-span-2">
                          <div className="col-span-8 sm:col-span-6">
                            <Field
                              name="note"
                              id="note"
                              rows={2}
                              as="textarea"
                              value={values.note}
                              placeholder="Text here will be displayed in the invoice"
                              className="mt-1 block w-full rounded-md border-2 border-[#4E6180]   bg-[#18222d] text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              onChange={(e) =>
                                setFieldValue('note', e.target.value)
                              }
                            />
                          </div>
                          <ErrorMessage
                            name="note"
                            component="div"
                            className="mt-2 text-xs text-rose-500"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white px-4 py-5 shadow dark:bg-slate-800 sm:rounded-lg sm:p-6">
              <div className="md:grid md:grid-cols-3 md:gap-6">
                <div className="md:col-span-1">
                  <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                    Services Provided
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-slate-300">
                    Input name, quantity, price and tax rate for each item.
                  </p>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-200"></p>
                </div>
                <div className="mt-5 md:col-span-2 md:mt-0">
                  <div className="col-span-8 sm:col-span-6">
                    <InvoiceLineItems
                      currency={resolveInvoiceCurrency(values)}
                      items={values.lineItems || []}
                      onItemsChange={(udaptedItems) => {
                        setFieldValue('lineItems', udaptedItems);
                      }}
                    />
                  </div>
                  <ErrorMessage
                    name="lineItems"
                    component="div"
                    className="mt-2 text-xs text-rose-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                color="indigo"
                type="submit"
                disabled={isSubmitting}
                isProcessing={isSubmitting}>
                {isEditMode ? 'Update Invoice' : 'Create Invoice'}
              </Button>
            </div>

            <FailModal
              show={invoiceFailFlag}
              onClose={() => {
                setInvoiceFailFlag(false);
              }}
            />

            <SuccessModal
              invoiceSuccessData={invoiceSuccessData}
              invoiceData={values}
              onClose={() => {
                setInvoiceSuccessData(null);
              }}
            />

            <ScrollToError />
          </Form>
        )}
      </Formik>
    </>
  );
};

export default CreateInvoiceForm;

function getRecurrenceUnit(interval) {
  return interval === 'month' ? 'Months' : 'Weeks';
}

function mapOrganationToFormDTO(organization) {
  return {
    userName: organization.name,
    userEmail: organization.email,
    userStreetAddress: organization.address,
    userCity: organization.city,
    userCountry: organization.country,
    userPostcode: organization.postcode,
    userVatNumber: organization.user_vat_number,
  };
}

function mapInvoiceToFormDTO(invoice) {
  return {
    userName: invoice.name,
    userEmail: invoice.user_email,
    userStreetAddress: invoice.user_first_line_address,
    userCity: invoice.user_city,
    userCountry: invoice.user_country,
    userPostcode: invoice.user_postal_code,
    userVatNumber: invoice.user_vat_number,
  };
}

function mapInvoiceToFormValues(invoice, clients) {
  const client = clients.find((cl) => cl.email === invoice.client_email);
  const selectedPaymentDetails = {};

  if (invoice.wallet_address) {
    selectedPaymentDetails.cryptoWallet = {
      address: invoice.wallet_address,
      currency: invoice.token,
      network: invoice.network,
      optionalNetworksCurrencies: invoice.optionalWallets
        ? invoice.optionalWallets.map((w) => {
            return {
              currency: w.token,
              network: upperCaseNetworkType(w.network),
            };
          })
        : [],
    };
  }

  if (invoice.bank_iban || invoice.bank_account_number) {
    selectedPaymentDetails.bankAccount = {
      currency: invoice.network === 'Fiat' ? invoice.token : undefined,
      accountBeneficiary: invoice.bank_account_beneficiary || '',
      bankName: invoice.bank_name || '',
      bankAddress: invoice.bank_address || '',
      bankBIC: invoice.bank_bic || '',
      accountIBAN: invoice.bank_iban || '',
      accountACHRoutingNumber: invoice.bank_ach_routing_number || '',
      accountNumber: invoice.bank_account_number || '',
    };
  }

  let paymentMethod = 'crypto';

  if (
    invoice.wallet_address &&
    (invoice.bank_iban || invoice.bank_account_number)
  ) {
    paymentMethod = 'multiple';
  } else if (invoice.bank_iban || invoice.bank_account_number) {
    paymentMethod = 'fiat';
  }

  return {
    userName: invoice.name,
    userEmail: invoice.user_email,
    userStreetAddress: invoice.user_first_line_address,
    userCity: invoice.user_city,
    userCountry: invoice.user_country,
    userPostcode: invoice.user_postal_code,
    userVatNumber: invoice.user_vat_number,

    clientName: invoice.client_name,
    clientEmail: invoice.client_email,
    clientStreetAddress: invoice.client_first_line_address,
    clientPostcode: invoice.client_postal_code,
    clientVatNumber: invoice.client_vat_number,
    clientCountry: invoice.client_country,
    clientCity: invoice.client_city,
    clientId: client ? client.id : undefined,

    lineItems: invoice.lineItems,

    selectedPaymentDetails,
    paymentMethod,

    includeNote: !!invoice.note,
    note: invoice.note || '',

    invoiceID: invoice.invoice_number,
    invoiceDate: dayjs(invoice.invoice_date).format('YYYY-MM-DD'),
    invoiceDueDate: dayjs(invoice.payments_date).format('YYYY-MM-DD'),
  };
}
